import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';
import { useWizard, WizardStep } from './WizardContext';
import Icon from '../common/Icon';
import { ICONS } from '../../utils/fontAwesome';

const WizardNavigation: React.FC = React.memo(() => {
  const { t, ready } = useTranslation();
  const { state, goToStep, canNavigateToStep } = useWizard();

  const steps: Array<{
    id: WizardStep;
    label: string;
    icon: IconProp;
  }> = [
    {
      id: 'load' as WizardStep,
      label: ready ? t('wizard.loadPhoto') : 'Load Photo',
      icon: ICONS.FOLDER_OPEN
    },
    {
      id: 'crop' as WizardStep,
      label: ready ? t('wizard.cropPhoto') : 'Crop Photo',
      icon: ICONS.CROP
    },
    {
      id: 'download' as WizardStep,
      label: ready ? t('wizard.downloadPhoto') : 'Save Photo',
      icon: ICONS.SAVE
    }
  ];

  const getStepStatus = (stepId: WizardStep) => {
    if (state.currentStep === stepId) return 'current';
    if (state.completedSteps.includes(stepId)) return 'completed';
    return 'pending';
  };

  const handleStepClick = (stepId: WizardStep) => {
    if (canNavigateToStep(stepId)) {
      goToStep(stepId);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm mb-6">
      <div className="mx-auto px-2 sm:px-4 w-full sm:w-[85%] md:w-[85%] lg:w-[85%] xl:w-[85%] 2xl:w-[85%]">
        <div className="py-4">
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-center space-x-8">
            {steps.map((step, index) => {
              const status = getStepStatus(step.id);
              const canNavigate = canNavigateToStep(step.id);
              const isClickable = canNavigate && status !== 'current';

              return (
                <React.Fragment key={step.id}>
                  <div 
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isClickable 
                        ? 'cursor-pointer group hover:bg-blue-50 dark:hover:bg-blue-900 hover:shadow-md transform hover:scale-105' 
                        : status === 'current'
                        ? 'bg-blue-50 dark:bg-blue-900 shadow-sm'
                        : ''
                    }`}
                    onClick={() => handleStepClick(step.id)}
                  >
                    {/* Step Circle */}
                    <div
                      className={`
                        flex items-center justify-center w-12 h-12 rounded-full text-sm font-medium transition-all duration-200
                        ${status === 'completed' 
                          ? 'bg-green-600 text-white shadow-lg' 
                          : status === 'current'
                          ? 'bg-blue-600 text-white shadow-lg'
                          : canNavigate 
                          ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 group-hover:bg-blue-200 dark:group-hover:bg-blue-700 group-hover:shadow-md border-2 border-blue-200 dark:border-blue-600 group-hover:border-blue-300 dark:group-hover:border-blue-500'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                        }
                      `}
                    >
                      {status === 'completed' ? (
                        <Icon icon={ICONS.CHECK} />
                      ) : status === 'current' ? (
                        <Icon icon={step.icon} />
                      ) : (
                        index + 1
                      )}
                    </div>
                    
                    {/* Step Label */}
                    <span
                      className={`
                        text-base font-semibold transition-all duration-200
                        ${status === 'current' 
                          ? 'text-blue-600' 
                          : status === 'completed'
                          ? 'text-green-600 group-hover:text-green-700'
                          : canNavigate 
                          ? 'text-blue-600 group-hover:text-blue-700'
                          : 'text-gray-400 dark:text-gray-500'
                        }
                      `}
                    >
                      {step.label}
                    </span>
                  </div>
                  
                  {/* Arrow between steps */}
                  {index < steps.length - 1 && (
                    <div className="text-gray-300 dark:text-gray-600">
                      <Icon icon={ICONS.ARROW_RIGHT} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <div className="flex items-center justify-between">
              {/* Current Step Info */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-full text-base font-semibold shadow-lg">
                  <Icon icon={steps.find(s => s.id === state.currentStep)?.icon || ICONS.FOLDER_OPEN} />
                </div>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {steps.find(s => s.id === state.currentStep)?.label}
                </span>
              </div>

              {/* Progress Indicator */}
              <div className="flex items-center space-x-1">
                {steps.map((step) => {
                  const status = getStepStatus(step.id);
                  return (
                    <div
                      key={step.id}
                      className={`w-3 h-3 rounded-full transition-all duration-200 ${
                        status === 'completed' 
                          ? 'bg-green-600 shadow-sm' 
                          : status === 'current'
                          ? 'bg-blue-600 shadow-md ring-2 ring-blue-200'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    />
                  );
                })}
              </div>
            </div>

            {/* Quick Navigation Buttons */}
            <div className="mt-3 flex space-x-2">
              {steps.map((step) => {
                const status = getStepStatus(step.id);
                const canNavigate = canNavigateToStep(step.id);
                const isClickable = canNavigate && status !== 'current';

                if (!canNavigate && status !== 'current') return null;

                return (
                  <button
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    disabled={!isClickable}
                    className={`
                      px-4 py-3 text-sm rounded-xl font-semibold transition-all duration-200 min-w-[100px] touch-manipulation
                      ${status === 'current'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : status === 'completed'
                        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800 active:bg-green-300 dark:active:bg-green-700 border-2 border-green-200 dark:border-green-700 hover:border-green-300 dark:hover:border-green-600 hover:shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }
                      ${isClickable ? 'cursor-pointer transform hover:scale-105 active:scale-95' : 'cursor-default'}
                    `}
                  >
                    <Icon icon={step.icon} className="mr-1" /> {step.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

WizardNavigation.displayName = 'WizardNavigation';

export default WizardNavigation;